'use client';

import { memo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import Link from 'next/link';

import ImageAsset from '@/components/elements/image-asset';
import useAmplitudeContext from '@/hooks/useAmplitudeContext';
import { useTranslation } from '@/i18n/client';
import { eventLoadingStatusSet } from '@/store/calendar-events/calendar-events.actions';
import { WithLang } from '@/types/general';
import { amplitudeEvents, storageKeys } from '@/utils/constants';
import { Cookies } from '@/utils/cookies';
import { getEventDates } from '@/utils/date-utils';
import { buildUrl } from '@/utils/navigation';
import { priceToFixed } from '@/utils/number-utils';
import { getRuCurrencyPrice, getYoutubeLink } from '@/utils/string-utils';

import { EventCardProps } from './event-cards-types';

const storeScrollPosition = () => window.sessionStorage.setItem(storageKeys.scrollPosition, window.scrollY.toString());

function EventCardDesktop(props: WithLang<EventCardProps>) {
    const { t: tCa } = useTranslation(props.lang, 'calendar');
    const { t: tCo } = useTranslation(props.lang, 'common');
    const { trackAmplitudeEvent } = useAmplitudeContext();

    const currentCode = Cookies.get(storageKeys.prefCountry) as string;

    const { startDate, endDate } = getEventDates()(props.event.start_date, props.event.duration);
    const dateStrings = {
        dateString: tCa('formats.dayMonthWeekday', {
            day: startDate.date(),
            month: startDate.month(),
            weekday: startDate.day(),
        }),
        time:
            `${startDate.hour()}:${startDate.minute().toString().padStart(2, '0')} – ` +
            `${endDate.hour()}:${endDate.minute().toString().padStart(2, '0')}`,
    };

    const priceElRef = useRef<HTMLParagraphElement>(null);
    const dispatch = useDispatch();

    const handleClick = () => {
        storeScrollPosition();
        dispatch(eventLoadingStatusSet(true));
        trackAmplitudeEvent(amplitudeEvents.EVENT_OPEN, { eventId: props.event.id, name: props.event.name });
    };

    return (
        <div
            id={props.event.id}
            className={`relative flex h-full min-h-[210px] w-full flex-col overflow-hidden rounded-lg opacity-100 ${
                props.event.status === 'past' ? 'sm:opacity-60' : ''
            }`}
        >
            <p
                ref={priceElRef}
                className="absolute right-0 z-[1] rounded-tr-lg bg-[#fad48c] px-4 py-2 text-xs shadow-md"
            >
                {tCo(`price.${props.event.price_type}`, {
                    context: priceToFixed(props.event.visitPrice),
                    //todo remove after audit
                    currency: getRuCurrencyPrice(currentCode, props.event.base_currency),
                })}
            </p>

            <div className="relative h-48 w-full overflow-hidden rounded-lg bg-gray-100">
                <ImageAsset
                    isAnimated={props.event.animated}
                    filenameDisk={props.event.image_cover?.filename_disk}
                    ids={[props.event.image_cover?.id, props.event.type?.image_cover]}
                    assetKey="companyLogo"
                    objectFit={'cover'}
                    className="blur-lg"
                />
                <Link
                    aria-label={`link to event ${props.event.name}`}
                    href={buildUrl('events', { children: props.event.slug ?? props.event.id })}
                    onClick={handleClick}
                    prefetch={false}
                    scroll={true}
                >
                    <div className="relative h-full w-full">
                        <ImageAsset
                            isAnimated={props.event.animated}
                            filenameDisk={props.event.image_cover?.filename_disk}
                            ids={[props.event.image_cover?.id, props.event.type?.image_cover]}
                            assetKey="card"
                        />
                    </div>
                </Link>
                {(props.event.youtube_video_link || props.event.for_adults) && (
                    <div
                        className="absolute bottom-0 left-0 z-[1] flex min-w-[72px] justify-center gap-x-2 bg-[#fad48c] px-2 py-1 shadow-md"
                        style={{ width: priceElRef.current?.clientWidth }}
                    >
                        {props.event.youtube_video_link && (
                            <Link
                                onClick={() =>
                                    trackAmplitudeEvent(amplitudeEvents.EVENT_CARD_YOUTUBE_LINK_CLICK, {
                                        eventId: props.event.id,
                                        name: props.event.name,
                                    })
                                }
                                aria-label={`link to youtube video ${props.event.name}`}
                                href={getYoutubeLink(props.event.youtube_video_link)}
                                target={'_blank'}
                                className="icon block h-6 w-6 bg-[#fad48c] bg-youtube"
                            />
                        )}
                        {props.event.for_adults && <span className="icon h-6 w-6 bg-[#fad48c] bg-adult" />}
                    </div>
                )}
            </div>
            <Link
                href={buildUrl('events', { children: props.event.slug ?? props.event.id })}
                prefetch={false}
                scroll={true}
            >
                <p className="line-clamp-2 hidden h-12 text-base font-semibold sm:block">{props.event.name}</p>
                <div className="mt-2 flex flex-col gap-y-2">
                    {props.event.location?.name && (
                        <span className="bg-geoposition bg-no-repeat pl-6 text-xs text-[#757575]">
                            {props.event.location.name}
                        </span>
                    )}
                    <p className="bg-calendar bg-no-repeat pl-6 text-xs text-[#757575]">
                        {dateStrings.dateString || ''}
                    </p>

                    <p className="bg-clock bg-no-repeat pl-6 text-xs text-[#757575]">{dateStrings.time || ''}</p>
                </div>
            </Link>
        </div>
    );
}

export default memo(EventCardDesktop);
